import React from 'react';

import { ReviewSection } from '~/components/AiChat/SideBarDialog/Actions/Action/ReviewAndExecute/ReviewSection';
import type { AiAction } from '~/components/AiChat/types';
import { isDynamicClaimActionTaskData } from '~/components/AiChat/types';
import Text from '~/components/core/TextComponents/Text';

import { PreviewSkeleton } from './PreviewSkeleton';
import type { ActionPreviewer } from './types';
import { useDocumentTemplatePreview } from './useDocumentTemplatePreview';

const DEFAULT_PREVIEW_UNAVAILABLE_NOTE = 'The preview is unavailable.';

/** A dynamic claim action's tool key → what its approval will produce. Add a tool here to preview it. */
export const ACTION_PREVIEWERS: Record<string, ActionPreviewer> = {
  create_document_from_template: { heading: 'Document preview', usePreview: useDocumentTemplatePreview },
};

interface ActionPreviewProps {
  action: AiAction;
  /** Merged over the preview's own classes, e.g. its height in a full-screen dialog. */
  previewClassName?: string;
}

const ActionPreviewSection: React.FC<ActionPreviewProps & { previewer: ActionPreviewer }> = ({
  action,
  previewClassName,
  previewer,
}) => {
  const { isLoading, content, unavailableNote } = previewer.usePreview(action, previewClassName);

  return (
    <ReviewSection heading={previewer.heading}>
      {isLoading ? (
        <PreviewSkeleton label={`Generating the ${previewer.heading.toLowerCase()}…`} className={previewClassName} />
      ) : (
        (content ?? (
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {unavailableNote ?? DEFAULT_PREVIEW_UNAVAILABLE_NOTE}
          </Text>
        ))
      )}
    </ReviewSection>
  );
};

const toolKeyOf = (action: AiAction) =>
  isDynamicClaimActionTaskData(action.task_data) ? action.task_data.tool_key : null;

export const hasActionPreview = (action: AiAction): boolean => {
  const toolKey = toolKeyOf(action);
  return !!toolKey && toolKey in ACTION_PREVIEWERS;
};

/** The preview of what approving the action will produce, for a tool that has one; nothing otherwise. */
export const ActionPreview: React.FC<ActionPreviewProps> = ({ action, previewClassName }) => {
  const toolKey = toolKeyOf(action);
  const previewer = toolKey ? ACTION_PREVIEWERS[toolKey] : undefined;

  // Keyed by tool so a different previewer's hooks never share this one's slot.
  return previewer ? (
    <ActionPreviewSection key={toolKey} action={action} previewClassName={previewClassName} previewer={previewer} />
  ) : null;
};
